import React, { Component, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { withStyles } from "@material-ui/core/styles";
import AttachmentIcon from "@material-ui/icons/Attachment";
import DescriptionIcon from "@material-ui/icons/Description";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import LocalizedStrings from "react-localization";
import Localizations from "./localizations.js";
import LanguageMenu from "./LanguageMenu.js";
import PrivacyPolicyDialog from "./PrivacyPolicyDialog";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SubdirectoryArrowRight from "@material-ui/icons/SubdirectoryArrowRight";
import BaseButton from "./components/BaseButton.js";
import CreateButton from "./components/CreateButton.js";
import Navbar from "./components/Navbar.js";
import NavBaseButton from "./components/NavBaseButton.js";
import FooterContainer from "./components/FooterContainer.js";
import InfoContainer from "./components/InfoContainer.js";
import Result from "./components/Result.js";
import AnalyzingFile from "./components/AnalyzingFile.js";
import SignatureHeader from "./components/SignatureHeader.js";
import { LANGUAGES, getLanguageFromURL } from "./utils/languageUtils.js";

import AtomiLogo from "./logos/atomi-validator-logo.svg";
import { ReactComponent as UnknownSigner } from "./svg/unknown-signer.svg";
import PoweredBy from "./studyo-logo-white.svg";
import AttentionImg from "./img/attention-image.svg";
import LoaderImgBlack from "./img/atomi-loader-black.png";
import "./css/dist/output.css";

// Language settings
let strings = new LocalizedStrings(Localizations);
const urlParams = new URLSearchParams(window.location.search);
const initialLanguage = getLanguageFromURL(urlParams);
strings.setLanguage(initialLanguage);

// Settings
let FileToValidate;
let url = "";
if (window.location.hostname === "localhost") {
  url = "https://validator-dev.studyo.fi/";
}

const studyoUrl = "https://studyo.fi";
const mainColor = "#006699";

//Mocking data for testing
const mock_validation_results = {
  failed: {
    eidas: false,
    lta: false,
    modificationsDetected: false,
    test: false,
    trusted: false,
    valid: false
  }
};

//Old stylings
const styles = {
  bgGradient: {
    default: "bg-gradient-to-t from-gray-200 to-gray-50",
    approved: "bg-gradient-to-t from-green-50 to-gray-50",
    failed: "bg-gradient-to-t from-red-50 to-gray-50"
  },
  root: {
    flexGrow: 1
  },
  grow: {
    flexGrow: 1
  },

  disabledButton: {
    backgroundColor: "rgba(68, 147, 255, 0.32) !important",
    border: "1px solid rgb(34,96,181, 0.2)",
    boxShadow: "none",
    width: "100%",
    maxWidth: "600px",
    fontFamily: "Montserrat",
    fontSize: "1.2rem",
    padding: "0.5rem 1rem",
    textTransform: "none",
    color: "#fff !important"
  },
  validateAgainButton: {
    backgroundColor: mainColor,
    width: "100%",
    maxWidth: "500px",
    fontFamily: "Montserrat",
    fontSize: "1.2rem",
    padding: "0.5rem 1rem",
    textTransform: "none",
    marginBottom: "1rem"
  },
  validatorWrapper: {
    maxWidth: "500px",
    width: "90%",
    margin: "0 auto",
    paddingBottom: 30,
    textAlign: "center",
    fontFamily: "Montserrat",
    fontWeight: "600",
    flexGrow: "2"
  },
  resultsWrapper: {
    maxWidth: "500px",
    width: "90%",
    margin: "0 auto",
    textAlign: "left",
    fontFamily: "Montserrat",
    fontWeight: "600",
    fontSize: "1.2rem"
  },

  vaMiddle: {
    verticalAlign: "middle"
  },
  expansionPanel: {
    boxShadow: "none",
    fontSize: "1rem"
  }
};

//Old components
function MyDropzone(state) {
  const { loading } = state;

  const [file, setFile] = useState({});
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles[0] != null) {
      setFile(acceptedFiles[0]);
      FileToValidate = acceptedFiles[0];
      state.updateValidateButtonClass();
    } else {
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "application/pdf"
  });

  let textForDrop;
  if (file.name == null) {
    textForDrop = isDragActive ? (
      <p>{strings.dragFile}</p>
    ) : (
      <p>{strings.chooseFile}</p>
    );
  } else {
    state = true;
  }

  return (
    <div
      className={`dropzone-container
      ${file.name !== undefined && !loading && "dropzone-active"}
      ${isDragActive && "dropzone-ondrag"}`}
      {...getRootProps()}
      role="button"
      tabIndex={0}
      aria-label={file.name ? `${strings.fileSelected} ${file.name}` : strings.chooseFile}
      onKeyDown={(e) => {
        // Allow Enter or Space to trigger file picker
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          // Trigger click on the hidden input
          const input = e.currentTarget.querySelector('input[type="file"]');
          if (input) input.click();
        }
      }}
    >
      <div
        className={
          "min-h-[140px] bg-transparent flex content-center flex-wrap flex-col justify-center justify-items-center"
        }
      >
        {loading ? (
          <AnalyzingFile />
        ) : (
          <div className={"flex content-center flex-wrap flex-col h-[100%]"}>
            <div className={"mb-4"}>
              {file.name == null || isDragActive ? (
                <AttachmentIcon
                  className={`self-center ${isDragActive && "text-blue-500"}`}
                  aria-hidden="true"
                />
              ) : (
                <DescriptionIcon className={"self-center text-green-500"} aria-hidden="true" />
              )}
            </div>
            {file.name !== null && (
              <p className={`text-sm font-normal`}>{file.name}</p>
            )}
            <input aria-describedby="textForDrop" {...getInputProps()} />
            <p id="textForDrop" className={`text-base font-normal`}>
              {textForDrop}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function getSignatoryLogo(logoURL) {
  const currentLogo = logoURL[strings.getLanguage()]; // TODO palauttaa undefined

  return `${url}/logos/${currentLogo}`;
}

function Results(props) {
  const { results } = props;

  let [signatory, issuerCN, issuerO, subjectCN] = [null, null, null, null];
  const signinDate = results.signingDate === null ? "N/A" : results.signingDate;

  const certificates =
    results.certificates == null ? [] : results.certificates.slice(1).reverse();

  const details = {
    certificates: certificates,
    subjectDN: results.subjectDN,
    signinDate: signinDate
  };

  function getValue(dn, prefix) {
    const element = dn.find((element) => element.trim().startsWith(prefix));
    return element ? element.trim().substring(prefix.length) : null;
  }

  if (results.issuerDN) {
    const splittedIssuerDN = results.issuerDN.split(",");
    issuerCN = getValue(splittedIssuerDN, "CN=");
    issuerO = getValue(splittedIssuerDN, "O=");
  }

  if (results.subjectDN) {
    const splittedSubjectDn = results.subjectDN.split(",");
    subjectCN = getValue(splittedSubjectDn, "CN=");
  }

  const signatoryLogo = () => {
    if (results.logo) {
      const unknownLogos = Object.values(results.logos).some(
        (logo) => logo === "unknown"
      );
      const isDvvLogo = results.logo === "dvv";

      return unknownLogos || isDvvLogo ? null : getSignatoryLogo(results.logos);
    }

    return null;
  };

  const signatureValid = results.trusted && results.valid;

  let logoHeader;

  if (results.logo === "dvv") {
    logoHeader = (
      <span style={styles.vaMiddle}>
        <p className=" font-light text-base text-gray-500 text-center">
          {results.givenName} {results.surName}
        </p>
        <p className=" font-light text-base text-gray-500 text-center">
          {results.organisation}
        </p>
      </span>
    );
  } else {
    logoHeader = signatoryLogo() ? (
      <img
        className="logo"
        src={signatoryLogo()}
        alt="{strings.signatoryLogoAlt}"
      />
    ) : (
      <UnknownSigner className="w-36 h-36 fill-gray-900 opacity-10" />
    );
  }

  const validSignatureHeader = (
    <SignatureHeader
      text={strings.signatureIsValid}
      signedBy={strings.signedBy}
      logoHeader={logoHeader}
    />
  );

  const unknownSignatureHeader = (
    <SignatureHeader
      text={strings.signatureIsValid}
      signedBy={strings.signedByUnknown}
      logoHeader={logoHeader}
    />
  );

  const invalidSignatureHeader = (
    <SignatureHeader
      text={strings.signatureIsNotValid}
      signedBy={strings.signedByUnknown}
      logoHeader={logoHeader}
      valid={false}
    />
  );

  if (signatureValid) {
    signatory = signatoryLogo ? validSignatureHeader : unknownSignatureHeader;
  } else if (results.valid) {
    signatory = validSignatureHeader;
  } else {
    signatory = invalidSignatureHeader;
  }

  return (
    <div className="flex flex-col flex-grow">
      <div className="flex flex-col content-center max-w-md m-auto pb-10">
        {signatory}

        {results.eidas && <Result variant="success" text={strings.eidas} />}

        {results.lta && <Result variant="success" text={strings.lta} />}

        {results.valid ? (
          <Result variant="success" text={strings.documentNotModified} />
        ) : (
          <Result variant="fail" text={strings.documentModified} />
        )}

        {results.trusted ? (
          <Result variant="success" text={strings.identityValid} />
        ) : (
          <Result variant="fail" text={strings.identityNotValid} />
        )}

        {results.test && (
          <h2 className="for_test_purposes">{strings.signatureForTest}</h2>
        )}

        {results.modificationsDetected && (
          <Result
            variant="warning"
            text="Dokumenttiin on tehty lisäyksiä (huomioita ja/tai muutoksia lomakekentissä) / Additional information is added to dokument (annotations and/or changes in form fields)"
          />
        )}

        {signatureValid && (
          <div className="mb-5">
            <ResultDetails details={details}></ResultDetails>
          </div>
        )}

        <div className=" flex flex-row self-center">
          <BaseButton onClick={props.clearValidation}>
            {strings.validateAnotherFile}
          </BaseButton>
        </div>
      </div>
    </div>
  );
}

function CertificateRow(props) {
  const classNameForRow = "row" + props.index;
  return (
    <span>
      <span className={["bold", classNameForRow].join(" ")}>
        <SubdirectoryArrowRight></SubdirectoryArrowRight>
        {props.certificate.subjectDN}
      </span>
      <br />
    </span>
  );
}

function ResultDetails(props) {
  let certificates = "";
  props.details.certificates.forEach(function (certificate, index) {
    if (index === 0) {
      certificates = certificate.subjectDN + "<br />";
    } else {
      for (let i = 1; i < index; i++) {
        certificates += "&nbsp;&nbsp;";
      }
      certificates += "|_" + certificate.subjectDN;
    }
  });

  return (
    <div id="result_details_root">
      <ExpansionPanel style={styles.expansionPanel}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <p className="text-sm font-normal text-gray-700">
            {strings.detailedInformation}
          </p>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <div className="text-sm font-normal text-gray-700">
            <div className="mb-5 ">
              <p>
                <span className="font-semibold">
                  {strings.signatureValidAndBy}
                </span>
                <br />

                {props.details.subjectDN}
              </p>
            </div>
            <p>
              <span className="font-semibold">{strings.signatureTime}</span>
              <br />
              {props.details.signinDate}
            </p>
            <p id="certificates">
              {strings.documentFromTrustedSource}
              <br />

              {props.details.certificates.map((certificate, i) => (
                <CertificateRow
                  key={i}
                  index={i}
                  certificate={certificate}
                ></CertificateRow>
              ))}
            </p>
          </div>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </div>
  );
}

//Main app
class App extends Component {
  constructor(props) {
    const { classes } = props;
    super(props);
    this.state = {
      validated: false,
      validationResults: {},
      validateable: false,
      validateButtonClass: classes.disabledButton,
      validationInProgress: false,
      backgroundGradient: styles.bgGradient.default,
      statusMessage: ""
    };
  }

  async upload() {
    try {
      this.setState({
        validationInProgress: true,
        statusMessage: strings.validationStarted
      });

      const data = new FormData();
      const file = FileToValidate;
      data.append("file", file);

      const response = await fetch(`${url}atomi`, {
        method: "POST",
        body: data
      });

      const validationResults = await response.json();

      // Generate accessible result summary
      const resultSummary = this.generateResultSummary(validationResults);

      this.setState({
        validated: true,
        validationResults,
        validationInProgress: false,
        statusMessage: resultSummary
      });
    } catch (error) {
      this.setState({
        validationInProgress: false,
        statusMessage: strings.validationError
      });
    }
  }

  generateResultSummary(results) {
    let summary = strings.validationComplete + " ";

    // Add signature validity
    if (results.valid && results.trusted) {
      summary += strings.signatureIsValid + ". ";
    } else {
      summary += strings.signatureIsNotValid + ". ";
    }

    // Add document modification status
    if (results.valid) {
      summary += strings.documentNotModified + " ";
    } else {
      summary += strings.documentModified + " ";
    }

    // Add identity validity
    if (results.trusted) {
      summary += strings.identityValid;
    } else {
      summary += strings.identityNotValid;
    }

    return summary;
  }

  setLanguage(language) {
    const currentLang = strings.getLanguage();
    if (currentLang !== language) {
      strings.setLanguage(language);
      // Update HTML lang attribute for accessibility
      document.documentElement.lang = language;
    }
    this.setState({});
  }

  clearValidation() {
    this.setState((state) => {
      return {
        validated: false,
        validationResults: {},
        statusMessage: ""
      };
    });
  }

  updateValidateButtonClass() {
    const { classes } = this.props;
    this.setState({
      validateButtonClass:
        FileToValidate == null ? classes.disabledButton : classes.validateButton,
      statusMessage: FileToValidate != null ? strings.fileSelected : ""
    });
  }

  getBackgroundGradient(validationResults) {

    if (validationResults) {
      switch (validationResults.trusted) {
        case true:
          this.setState({
            backgroundGradient: styles.bgGradient.approved
          });
          break;
        case false:
          this.setState({
            backgroundGradient: styles.bgGradient.failed
          });
          break;
        default:
          this.setState({
            backgroundGradient: styles.bgGradient.default
          });
          break;
      }
    }
  }

  componentDidMount() {
    // Set initial HTML lang attribute based on URL parameter or default
    const currentLang = strings.getLanguage();
    document.documentElement.lang = currentLang;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.validationResults !== this.state.validationResults) {
      this.getBackgroundGradient(this.state.validationResults);
    }
  }

  render() {
    document.title = "Atomi Validator";
    const { classes } = this.props;

    return (
      <div id="validator" className={this.state.backgroundGradient}>
        {/* ARIA live region for status announcements */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: '0',
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            borderWidth: '0'
          }}
        >
          {this.state.statusMessage}
        </div>

        <Navbar
          back={
            <>
              {this.state.validated && (
                <NavBaseButton onClick={this.clearValidation.bind(this)}>
                  {strings.backButton}
                </NavBaseButton>
              )}
            </>
          }
          logo={
            <img
              className="absolute top-5 left-1/2 right-1/2 transform -translate-x-1/2 -translate-y-1/2"
              src={AtomiLogo}
              alt="ATOMI logo"
            />
          }
          menu={
            <LanguageMenu
              className={classes.langSelectBtn}
              strings={strings}
              setLanguage={this.setLanguage.bind(this)}
            ></LanguageMenu>
          }
        />

        {this.state.validated ? (
          <Results
            results={this.state.validationResults}
            clearValidation={this.clearValidation.bind(this)}
          />
        ) : (
          <div className={classes.validatorWrapper}>
            <h1
              tabIndex="0"
              className="text-2xl font-normal text-gray-800 m-10 mt-20"
            >
              {strings.infotext}
            </h1>

            <MyDropzone
              updateValidateButtonClass={this.updateValidateButtonClass.bind(
                this
              )}
              loading={this.state.validationInProgress}
            ></MyDropzone>

            <CreateButton
              loading={this.state.validationInProgress}
              customLoader={
                <span className="flex items-center">
                  <div>
                    <img id="atomi-loader" src={LoaderImgBlack} alt="Logo" />
                  </div>
                  <div className="ml-2">
                    <p className="ml-2">Tarkistetaan...</p>
                  </div>
                </span>
              }
              disabled={
                this.state.validateButtonClass === classes.disabledButton
              }
              onClick={this.upload.bind(this)}
            >
              {strings.validateFile}
            </CreateButton>
          </div>
        )}

        <InfoContainer
          signatureIsValid={this.state.validationResults.trusted}
          tabIndex="0"
        >
          <div className="font-normal text-sm text-gray-500 text-center max-w-lg ">
            <div
              className={"flex justify-center align-middle content-center p-5"}
            >
              <img src={AttentionImg} aria-hidden="true" />
            </div>
            <h2 className={" text-2xl font-light text-gray-500"}>
              {strings.attention}
            </h2>
            <br />
            <p className={"text-base"}>{strings.dataCollectingInfo}</p>
            <br />
            <PrivacyPolicyDialog
              strings={strings}
              linkTextId="readMore"
            ></PrivacyPolicyDialog>
          </div>
        </InfoContainer>

        <FooterContainer>
          <div
            className={
              "flex justify-center align-middle content-center h-[100%] items-center"
            }
          >
            <a
              href={studyoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={"hover:opacity-50"}
            >
              <img
                src={PoweredBy}
                alt="Powered by Studyo"
                width="100"
                height="100%"
              />
            </a>
          </div>
        </FooterContainer>
      </div>
    );
  }
}

export default withStyles(styles)(App);
